import { UserApiError, type AccountLifecycleMutation, type AccountSecurityChallenge } from '@/api/client'
import type { IdentityContext } from '@/api/request-context'
import type { Account, AccountSecurity, CloudSession, Device } from '@/types'

export const accountTabs = ['profile', 'devices', 'sessions', 'mfa', 'recovery', 'lifecycle'] as const
export type AccountTab = typeof accountTabs[number]
export type AccountSearch = { device?: string; locale?: string; lifecycleRetry?: string }
export type BindingChallenge = AccountSecurityChallenge & { expiresAt: number; resendAt: number }

export function accountTabFromHash(hash: string): AccountTab {
  switch (hash.replace(/^#/, '')) {
    case 'devices': case 'login-devices': return 'devices'
    case 'sessions': return 'sessions'
    case 'mfa': case 'security': return 'mfa'
    case 'recovery': return 'recovery'
    case 'lifecycle': case 'delete-account': return 'lifecycle'
    default: return 'profile'
  }
}
export function nextAccountTab(tab: AccountTab, key: string): AccountTab | undefined {
  const index = accountTabs.indexOf(tab)
  if (key === 'ArrowRight' || key === 'ArrowDown') return accountTabs[(index + 1) % accountTabs.length]
  if (key === 'ArrowLeft' || key === 'ArrowUp') return accountTabs[(index + accountTabs.length - 1) % accountTabs.length]
  if (key === 'Home') return accountTabs[0]
  if (key === 'End') return accountTabs[accountTabs.length - 1]
}
export function parseAccountSearch(input: Record<string, unknown>): AccountSearch {
  return {
    ...(typeof input.device === 'string' && input.device.trim() && input.device.length <= 128 ? { device: input.device } : {}),
    ...(input.locale === 'zh-CN' || input.locale === 'en-US' ? { locale: input.locale } : {}),
    // This marker only requests URL cleanup. It never authorizes a mutation.
    ...(input.lifecycleRetry !== undefined ? { lifecycleRetry: 'discard' } : {}),
  }
}
export const validSecurityVersion = (value: unknown): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value >= 0
export const canManageDevice = (device: Device) => !device.current && device.status === 'ACTIVE'
export const canRevokeSession = (session: CloudSession) => !session.current
export const recoverableDevices = (devices: Device[]) => devices.filter(canManageDevice)
export const orderDevices = (devices: Device[]) => [...devices].sort((left, right) => Number(right.current) - Number(left.current))
export const maskedLoginName = (value: string) => /^sms_[0-9a-f]{16,}$/i.test(value) ? `sms_…${value.slice(-8)}` : value
export function normalizeBindingPhone(value: string): string | undefined {
  const phone = value.replace(/[\s-]/g, '').replace(/^\+86/, '')
  return /^1[3-9]\d{9}$/.test(phone) ? `+86${phone}` : undefined
}
export function bindingChallenge(value: AccountSecurityChallenge, accountId: string, now = Date.now()): BindingChallenge {
  if (!value || typeof value.challengeId !== 'string' || !value.challengeId.trim() || value.challengeId.length > 128
    || typeof value.bindingId !== 'string' || value.bindingId.length > 128 || !value.bindingId.startsWith(`${accountId}.`)
    || !Number.isSafeInteger(value.expiresInSeconds) || value.expiresInSeconds <= 0
    || !Number.isSafeInteger(value.resendAfterSeconds) || value.resendAfterSeconds < 0
    || !Number.isSafeInteger(now + value.expiresInSeconds * 1000) || !Number.isSafeInteger(now + value.resendAfterSeconds * 1000)) {
    throw new UserApiError(502, 'invalid_account_security_challenge')
  }
  return { ...value, expiresAt: now + value.expiresInSeconds * 1000, resendAt: now + value.resendAfterSeconds * 1000 }
}
export function verifyAccountIdentity<T extends Account | AccountSecurity>(value: T, identity: IdentityContext): T {
  const id = 'id' in value ? value.id : value.accountId
  if (!identity.accountId || id !== identity.accountId || ('securityVersion' in value && !validSecurityVersion(value.securityVersion))) {
    throw new UserApiError(502, 'account_identity_response_invalid')
  }
  return value
}
export function verifyLifecycleResult(value: AccountLifecycleMutation, identity: IdentityContext, operation: 'delete' | 'cancel' | 'recover', priorVersion: number): AccountLifecycleMutation {
  const expectedStatus = operation === 'delete' ? 'pending_deletion' : 'active'
  if (!value || value.accountId !== identity.accountId || value.status !== expectedStatus
    || !validSecurityVersion(value.securityVersion) || !validSecurityVersion(priorVersion) || value.securityVersion < priorVersion
    || (operation === 'delete' && (!value.deletionDueAt || !Number.isFinite(Date.parse(value.deletionDueAt))))
    || (operation === 'recover' && (!value.replacementDeviceId || typeof value.replacementDeviceId !== 'string'))) {
    throw new UserApiError(502, 'account_lifecycle_response_invalid')
  }
  return value
}
